import { VectorIcon } from '@panwds/icons';
import { classNames, tw, twMerge } from '@panwds/tailwindcss';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { mergeRefs } from '../../utils/index.js';
import { CopyButton } from '../Button/index.js';
import { DangerIcon, SuccessIcon } from '../Icon/SemanticIcons.js';
import { ClearButton } from '../Input/ClearButton.js';
import { iconContainerPadding, resizeIconContainerPadding, rootPadding, selectBorderColor, textBoxPadding } from './textarea.logic.js';
import { useTextareaAutosize } from './useTextareaAutosize.js';
const textSizes = {
    sm: tw `twds-text-xs`,
    md: tw `twds-text-xs`,
    lg: tw `twds-text-sm`
};
const gapSizes = {
    sm: tw `twds-gap-0.5`,
    md: tw `twds-gap-1`,
    lg: tw `twds-gap-1`
};
export const Textarea = forwardRef(({ allowCopy = false, allowClear = false, addClassName, appearance, disabled, dataMetrics, dataTestId, onChange, readOnly, rowsMax, rowsMin = 1, value, isResizable = true, size = 'md', ...passThrough }, userRef) => {
    const isControlled = useRef(value !== undefined);
    const textareaRef = useRef(null);
    const ref = mergeRefs([textareaRef, userRef]);
    const hasError = appearance === 'error';
    const isSuccess = appearance === 'success';
    const rootClassName = twMerge('twds-flex twds-items-center twds-border-solid twds-border twds-rounded twds-box-border', selectBorderColor({ hasError, readOnly, disabled }), readOnly
        ? 'twds-bg-black dark:twds-bg-white twds-bg-opacity-[0.04] dark:twds-bg-opacity-[0.04]'
        : 'twds-bg-white dark:twds-bg-black twds-bg-opacity-80 dark:twds-bg-opacity-16', disabled && 'twds-text-disabled twds-cursor-not-allowed', !isResizable && (isSuccess || hasError || allowClear || allowCopy) && rootPadding(size), gapSizes[size], addClassName);
    const textareaClassName = twMerge('twds-font-lato twds-border-0 focus:twds-outline-none twds-placeholder-gray-500 dark:twds-placeholder-gray-400 dark:twds-text-gray-100 twds-flex-auto twds-bg-transparent twds-min-w-16 twds-max-w-full twds-w-full', '[&::-webkit-resizer]:twds-hidden hover:twds-resize-none', disabled ? 'twds-cursor-not-allowed twds-text-disabled dark:twds-text-gray-600' : 'twds-text-gray-900', readOnly && 'dark:twds-text-gray-100', textBoxPadding(size, !(isResizable || isSuccess || hasError || allowClear || allowCopy)), textSizes[size]);
    const { height, shadowEl, syncHeight } = useTextareaAutosize({
        className: textareaClassName,
        rowsMax,
        rowsMin,
        textareaRef
    });
    useEffect(() => {
        syncHeight();
    }, [value]);
    const handleChange = (event) => {
        if (!isControlled.current) {
            syncHeight();
        }
        if (onChange) {
            onChange(event);
        }
    };
    const mouseDownHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const startY = e.clientY;
        const startHeight = textareaRef.current?.offsetHeight || 0;
        const onMouseMove = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const newHeight = startHeight + e.clientY - startY;
            if (textareaRef.current) {
                textareaRef.current.style.height = `${newHeight}px`;
                document.body.classList.add(...['[&_*]:twds-cursor-se-resize', 'twds-cursor-se-resize']);
            }
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.classList.remove(...['[&_*]:twds-cursor-se-resize', 'twds-cursor-se-resize']);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [textareaRef]);
    return (React.createElement("span", { "data-metrics": dataMetrics, "data-testid": dataTestId || 'textarea', className: rootClassName },
        React.createElement("textarea", { disabled: disabled, readOnly: readOnly, ref: ref, rows: rowsMin, className: textareaClassName, onChange: handleChange, style: { height }, value: value, ...passThrough }),
        allowClear && (React.createElement("div", { className: classNames('twds-flex twds-flex-col twds-pl-0 twds-self-stretch twds-justify-start', iconContainerPadding(size), 'twds-px-0') },
            React.createElement(ClearButton, { addClassName: "twds-p-0.5", style: { marginRight: '-0.125rem' }, iconSize: size, onClick: (e) => {
                    if (isControlled.current) {
                        if (textareaRef.current && onChange) {
                            onChange(makeMockClearEvent(textareaRef.current, e));
                        }
                    }
                    else if (textareaRef.current) {
                        textareaRef.current.value = '';
                    }
                } }))),
        allowCopy && (React.createElement("div", { className: classNames('twds-flex twds-flex-col twds-pl-0 twds-self-stretch twds-justify-start', iconContainerPadding(size), 'twds-px-0') },
            React.createElement(CopyButton, { iconSize: size, addClassName: "twds-p-0.5", style: { marginRight: '-0.125rem', width: '1.5rem', height: '1.5rem' }, text: () => textareaRef.current?.value }))),
        isSuccess && (React.createElement("div", { className: classNames('twds-flex twds-flex-col twds-pl-0 twds-self-stretch twds-justify-start', iconContainerPadding(size), 'twds-px-0') },
            React.createElement(SuccessIcon, { size: "lg" }))),
        hasError && (React.createElement("div", { className: classNames('twds-flex twds-flex-col twds-pl-0 twds-self-stretch twds-justify-start', iconContainerPadding(size), 'twds-px-0') },
            React.createElement(DangerIcon, { size: "lg" }))),
        isResizable && (React.createElement("div", { className: classNames('[&_svg]:twds-w-[6px] [&_svg]:twds-h-[6px] twds-flex twds-flex-col twds-pl-0 twds-self-stretch twds-justify-end', resizeIconContainerPadding(size), 'twds-pl-0') },
            React.createElement("span", { className: "twds-cursor-se-resize", onMouseDown: mouseDownHandler },
                React.createElement(VectorIcon, { size: "xxs" })))),
        shadowEl));
});
Textarea.displayName = 'Textarea';
function makeMockClearEvent(inputRef, clickEvent) {
    const updatedTarget = { ...inputRef, value: '' };
    const { nativeEvent, bubbles, cancelable, defaultPrevented, eventPhase, isTrusted, preventDefault, isDefaultPrevented, stopPropagation, isPropagationStopped, persist, timeStamp } = clickEvent;
    return {
        currentTarget: updatedTarget,
        target: updatedTarget,
        nativeEvent,
        bubbles,
        cancelable,
        defaultPrevented,
        eventPhase,
        isTrusted,
        preventDefault,
        isDefaultPrevented,
        stopPropagation,
        isPropagationStopped,
        persist,
        timeStamp,
        type: 'clear'
    };
}
//# sourceMappingURL=Textarea.js.map